/**
 * How a connected account is named on screen.
 *
 * A Dice login IS its phone number, so the number is the account's identity
 * rather than a contact detail — and two accounts routinely share a last-four,
 * which is exactly when someone syncs, transfers from, or disconnects the wrong
 * one. So the whole number is printed wherever we have it.
 *
 * `phone` is owner-only: the server drops it from a seat member's copy of the
 * accounts response and sends only `phone_masked`. Nothing here may assume
 * `phone` is present — that is the entire reason these helpers exist instead of
 * every call site reaching for one field or the other.
 */

/** The shape both `ExternalAccount` and the narrower inline account types fit. */
export interface AccountIdentityFields {
  id: number;
  email?: string | null;
  /** Whole number. Absent for a seat member. */
  phone?: string | null;
  /** Last four (***1234). Sent to everyone. */
  phone_masked?: string | null;
}

/** The number to print: whole if we have it, last-four if we don't. */
export const accountPhone = (acct: AccountIdentityFields): string | null =>
  acct.phone || acct.phone_masked || null;

/** "email · +15551234567", either half optional, id as the last resort. */
export const accountIdentity = (acct: AccountIdentityFields): string => {
  const phone = accountPhone(acct);
  if (acct.email && phone) return `${acct.email} · ${phone}`;
  return acct.email || phone || `Account ${acct.id}`;
};
